/**
 * PATH: packages/bills/src/components/BillPoPanel.tsx
 * FILE_ID: BILLS-PKG-CMP-PO-002
 * PURPOSE: Right-rail "PO" tab — allocate POs to a bill + line-level reconciliation.
 *
 * REPLACES the v001 stub (which just showed the supplier reference +
 * an "Auto-match coming soon" callout). v002 ships the real flow:
 *
 *   1. Reconciliation summary  — bill total, allocated, remainder.
 *   2. Linked POs              — existing allocations with per-line
 *                                variance grid (qty Δ, $ Δ, flag).
 *   3. Available POs           — open POs on the bill's linked job;
 *                                tick + Allocate to add one.
 *   4. Suggestions             — top-10 ranked auto-match candidates
 *                                from the backend (ref / total / vendor).
 *   5. Create PO inline form   — manual entry against the linked job.
 *
 * Each section degrades gracefully:
 *   - No linked job → "Available POs" hidden, "Create PO" requires
 *     a job picker (out of scope for PR-1; rendered with a hint).
 *   - Client method missing (e.g. `createPurchaseOrder` undefined)
 *     → that affordance is hidden with no error.
 *
 * Backend contract: organisation/views/po_views/ (see endpoints.ts).
 *
 * STATE STRATEGY: lift the loading/mutation state into local
 * `useState` hooks rather than TanStack Query because the package is
 * transport-agnostic (apps choose their own caching layer). Each
 * mutation refetches the affected lists in-place.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBillsClient } from '../client';
import type {
  BillDetail,
  BillLineItem,
  BillPoAllocation,
  BillPoAllocationsResponse,
  BillPoLineMatch,
  BillPoVarianceRow,
  CreateBillPoAllocationInput,
  CreatePurchaseOrderInput,
  PurchaseOrder,
  PurchaseOrderLine,
} from '../types';
import {
  SourceBadge,
  StatusBadge,
  FlagBadge,
  fmtDelta,
  Section,
  Field,
  Stat,
  Empty,
  inputCls,
  btnPrimary,
  btnSecondary,
  btnGhost,
} from './_primitives';
import {
  PaymentStatusBadge,
  PaymentsExpander,
} from './_payments';


interface Props {
  bill: BillDetail | null;
}

/** Cap on how many rows we render in the "available POs" list. The
 * server caps at 200 already; we trim to the most relevant 50 in the
 * UI to keep the panel scannable. */
const AVAILABLE_PO_LIMIT = 50;

export function BillPoPanel({ bill }: Props) {
  if (!bill) {
    return <Empty>Select a bill to view its purchase-order context.</Empty>;
  }
  return <BillPoPanelInner bill={bill} />;
}

function BillPoPanelInner({ bill }: { bill: BillDetail }) {
  const client = useBillsClient();
  const jobId = bill.email_job_id;

  /* ─── allocations + reconciliation summary ─────────────────────── */
  const [recon, setRecon] = useState<BillPoAllocationsResponse | null>(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState<string | null>(null);

  const refreshAllocations = useCallback(async () => {
    if (!client.listBillPoAllocations) return;
    setReconLoading(true);
    setReconError(null);
    try {
      const r = await client.listBillPoAllocations(bill.id);
      setRecon(r);
    } catch (e: unknown) {
      setReconError(e instanceof Error ? e.message : 'Failed to load PO allocations');
    } finally {
      setReconLoading(false);
    }
  }, [bill.id, client]);

  useEffect(() => {
    void refreshAllocations();
  }, [refreshAllocations]);

  /* ─── available POs for the linked job ─────────────────────────── */
  const [availablePos, setAvailablePos] = useState<PurchaseOrder[]>([]);
  const [availableLoading, setAvailableLoading] = useState(false);

  const refreshAvailable = useCallback(async () => {
    if (!jobId || !client.listPurchaseOrdersForJob) return;
    setAvailableLoading(true);
    try {
      const r = await client.listPurchaseOrdersForJob(jobId);
      setAvailablePos(r.purchase_orders.slice(0, AVAILABLE_PO_LIMIT));
    } catch {
      setAvailablePos([]);
    } finally {
      setAvailableLoading(false);
    }
  }, [client, jobId]);

  useEffect(() => {
    void refreshAvailable();
  }, [refreshAvailable]);

  /* ─── suggestions ──────────────────────────────────────────────── */
  const [suggestions, setSuggestions] = useState<PurchaseOrder[]>([]);
  useEffect(() => {
    if (!client.getBillPoSuggestions) return;
    let cancelled = false;
    client
      .getBillPoSuggestions(bill.id)
      .then((r) => {
        if (!cancelled) setSuggestions(r.suggestions);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [bill.id, client]);

  /* ─── allocation mutation ──────────────────────────────────────── */
  const [busyAllocPoId, setBusyAllocPoId] = useState<number | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const handleAllocate = useCallback(
    async (po: PurchaseOrder) => {
      if (!client.upsertBillPoAllocation) return;
      setBusyAllocPoId(po.id);
      setMutationError(null);
      try {
        // Auto-pair lines by description fuzzy match (case-insensitive
        // contains in either direction). Bookkeeper can adjust per-row
        // afterwards. Falls back to positional pairing when neither
        // side has descriptions.
        const matches = autoPairLines(bill.line_items || [], po.line_items ?? []);
        const input: CreateBillPoAllocationInput = {
          purchase_order_id: po.id,
          // Default the allocated_amount to the bill total so the
          // bookkeeper's first click closes the loop. They can split
          // across multiple POs by editing the amount afterwards.
          allocated_amount: bill.total || po.total || '0.00',
          line_matches: matches,
        };
        await client.upsertBillPoAllocation(bill.id, input);
        await Promise.all([refreshAllocations(), refreshAvailable()]);
      } catch (e: unknown) {
        setMutationError(e instanceof Error ? e.message : 'Failed to allocate PO');
      } finally {
        setBusyAllocPoId(null);
      }
    },
    [bill.id, bill.line_items, bill.total, client, refreshAllocations, refreshAvailable],
  );

  const handleUnallocate = useCallback(
    async (allocationId: string) => {
      if (!client.deleteBillPoAllocation) return;
      setMutationError(null);
      try {
        await client.deleteBillPoAllocation(bill.id, allocationId);
        await Promise.all([refreshAllocations(), refreshAvailable()]);
      } catch (e: unknown) {
        setMutationError(e instanceof Error ? e.message : 'Failed to unallocate PO');
      }
    },
    [bill.id, client, refreshAllocations, refreshAvailable],
  );

  /* ─── manual create PO ─────────────────────────────────────────── */
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreate = useCallback(
    async (input: CreatePurchaseOrderInput) => {
      if (!client.createPurchaseOrder) return;
      setMutationError(null);
      try {
        await client.createPurchaseOrder({ ...input, job_id: jobId ?? null });
        setCreateOpen(false);
        await refreshAvailable();
      } catch (e: unknown) {
        setMutationError(e instanceof Error ? e.message : 'Failed to create PO');
      }
    },
    [client, jobId, refreshAvailable],
  );

  /* ─── render ───────────────────────────────────────────────────── */

  const allocatedPoIds = useMemo(
    () => new Set((recon?.allocations || []).map((a) => a.purchase_order.id)),
    [recon],
  );

  // Filter suggestions + available so we don't show a PO twice.
  const visibleAvailable = availablePos.filter((p) => !allocatedPoIds.has(p.id));
  const visibleSuggestions = suggestions.filter((p) => !allocatedPoIds.has(p.id));

  return (
    <aside className="flex h-full min-h-0 w-full flex-col border-l border-border bg-card">
      <header className="shrink-0 border-b border-border bg-card px-3 py-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Purchase orders
        </p>
        <h2 className="truncate text-sm font-semibold tracking-tight">
          {bill.vendor_name || 'Untitled vendor'}
          {bill.invoice_number ? ` · Bill #${bill.invoice_number}` : ''}
        </h2>
        {jobId ? (
          <p className="truncate text-xs text-muted-foreground">
            Linked to job{' '}
            <a
              href={`/jobs/${jobId}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              #{jobId}
            </a>
          </p>
        ) : (
          <p className="truncate text-xs text-amber-700 dark:text-amber-300">
            Bill not linked to a job — link from the Job Attribution tab to see
            available POs.
          </p>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {mutationError && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {mutationError}
          </div>
        )}
        {reconError && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {reconError}
          </div>
        )}

        <ReconciliationSummary recon={recon} loading={reconLoading} />

        <LinkedPosSection
          allocations={recon?.allocations || []}
          variance={recon?.variance || []}
          billLineItems={recon?.bill_line_items || bill.line_items || []}
          onUnallocate={handleUnallocate}
          loading={reconLoading}
        />

        {jobId && (
          <AvailablePosSection
            title="Available POs for this job"
            pos={visibleAvailable}
            loading={availableLoading}
            onAllocate={handleAllocate}
            busyAllocPoId={busyAllocPoId}
            emptyHint="No open POs found on this job. Create one below or sync from Xero."
          />
        )}

        {visibleSuggestions.length > 0 && (
          <AvailablePosSection
            title="Auto-match suggestions"
            subtitle="Ranked by reference, total, and vendor proximity."
            pos={visibleSuggestions}
            loading={false}
            onAllocate={handleAllocate}
            busyAllocPoId={busyAllocPoId}
            emptyHint=""
          />
        )}

        {client.createPurchaseOrder && (
          <section className="mb-2">
            <button
              type="button"
              className={createOpen ? btnSecondary : btnPrimary}
              onClick={() => setCreateOpen((v) => !v)}
            >
              {createOpen ? 'Cancel' : '+ Create new PO'}
            </button>
            {createOpen && (
              <CreatePoForm
                bill={bill}
                jobId={jobId ?? null}
                onSubmit={handleCreate}
              />
            )}
          </section>
        )}
      </div>
    </aside>
  );
}

/* ─────────────────────────── Sub-components ───────────────────────── */

function ReconciliationSummary({
  recon,
  loading,
}: {
  recon: BillPoAllocationsResponse | null;
  loading: boolean;
}) {
  if (!recon) {
    return (
      <Section title="Reconciliation">
        <p className="text-xs italic text-muted-foreground">
          {loading ? 'Loading…' : 'No allocations yet.'}
        </p>
      </Section>
    );
  }
  const { totals } = recon;
  const remainder = parseFloat(totals.unallocated_remainder);
  const remainderColor =
    Math.abs(remainder) < 0.01
      ? 'text-emerald-700 dark:text-emerald-400'
      : remainder > 0
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-rose-700 dark:text-rose-400';
  return (
    <Section title="Reconciliation">
      <div className="grid grid-cols-3 gap-2 text-xs">
        <Stat label="Bill total" value={`${totals.currency} ${totals.bill_total}`} />
        <Stat
          label="Allocated"
          value={`${totals.currency} ${totals.allocated_total}`}
        />
        <Stat
          label="Remainder"
          value={`${totals.currency} ${totals.unallocated_remainder}`}
          valueClass={remainderColor}
        />
      </div>
    </Section>
  );
}

function LinkedPosSection({
  allocations,
  variance,
  billLineItems,
  onUnallocate,
  loading,
}: {
  allocations: BillPoAllocation[];
  variance: BillPoVarianceRow[];
  billLineItems: BillLineItem[];
  onUnallocate: (allocationId: string) => Promise<void>;
  loading: boolean;
}) {
  if (loading && allocations.length === 0) {
    return (
      <Section title="Linked POs">
        <p className="text-xs italic text-muted-foreground">Loading…</p>
      </Section>
    );
  }
  if (allocations.length === 0) {
    return (
      <Section title="Linked POs">
        <p className="text-xs italic text-muted-foreground">
          No POs linked to this bill yet.
        </p>
      </Section>
    );
  }
  return (
    <Section title={`Linked POs (${allocations.length})`}>
      <div className="space-y-3">
        {allocations.map((a) => {
          const rows = variance.filter((v) => v.allocation_id === a.id);
          return (
            <LinkedPoCard
              key={a.id}
              allocation={a}
              varianceRows={rows}
              billLineItems={billLineItems}
              onUnallocate={() => onUnallocate(a.id)}
            />
          );
        })}
      </div>
    </Section>
  );
}

function LinkedPoCard({
  allocation,
  varianceRows,
  billLineItems,
  onUnallocate,
}: {
  allocation: BillPoAllocation;
  varianceRows: BillPoVarianceRow[];
  billLineItems: BillLineItem[];
  onUnallocate: () => Promise<void>;
}) {
  const po = allocation.purchase_order;
  const flagged = varianceRows.some((r) => r.flag !== 'ok');
  return (
    <div className="rounded-md border border-border bg-background p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-xs font-semibold">
              {po.purchase_order_number || `PO #${po.id}`}
            </span>
            <SourceBadge source={po.source} />
            <PaymentStatusBadge
              status={po.payment_status}
              paid={po.amount_paid}
              total={po.total}
              currency={po.currency_code}
            />
            {flagged && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                variance
              </span>
            )}
          </div>
          <p className="truncate text-[11px] text-muted-foreground">
            {po.contact_name || 'Unknown supplier'} · {po.currency_code} {po.total}
          </p>
          {po.reference && (
            <p className="truncate text-[11px] text-muted-foreground">
              Ref: {po.reference}
            </p>
          )}
        </div>
        <button
          type="button"
          className={btnGhost}
          title="Unallocate this PO"
          onClick={() => void onUnallocate()}
        >
          Unallocate
        </button>
      </div>

      {varianceRows.length > 0 ? (
        <VarianceGrid rows={varianceRows} />
      ) : (
        <p className="mt-2 text-[11px] italic text-muted-foreground">
          No line-level matches recorded — header-only allocation.
        </p>
      )}

      {/* Coverage hint when there are bill lines NOT matched to any
          PO line on this allocation. Helps the bookkeeper notice
          unmatched freight / discount lines. */}
      <UnmatchedLinesHint
        billLineItems={billLineItems}
        matched={new Set(varianceRows.map((r) => r.bill_line_idx))}
      />

      {allocation.notes && (
        <p className="mt-2 rounded bg-muted/50 px-2 py-1 text-[11px] text-foreground">
          {allocation.notes}
        </p>
      )}

      {/* Payments side — collapsed by default. Bookkeeper expands it
          when they need to record a deposit/COD payment or check
          existing payments against this PO. See PR-2 (2026-05-05). */}
      <PaymentsExpander po={po} />
    </div>
  );
}

function VarianceGrid({ rows }: { rows: BillPoVarianceRow[] }) {
  return (
    <div className="mt-2 overflow-x-auto rounded border border-border">
      <table className="min-w-full text-[11px]">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Line</th>
            <th className="px-2 py-1 text-right font-medium">Qty Δ</th>
            <th className="px-2 py-1 text-right font-medium">Unit Δ</th>
            <th className="px-2 py-1 text-right font-medium">Amount Δ</th>
            <th className="px-2 py-1 text-left font-medium">Flag</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-t border-border">
              <td className="max-w-[10rem] truncate px-2 py-1" title={r.bill_description}>
                {r.bill_description || `Line ${r.bill_line_idx + 1}`}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {fmtDelta(r.qty_diff)}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {fmtDelta(r.unit_price_diff)}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {fmtDelta(r.amount_diff)}
              </td>
              <td className="px-2 py-1">
                <FlagBadge flag={r.flag} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UnmatchedLinesHint({
  billLineItems,
  matched,
}: {
  billLineItems: BillLineItem[];
  matched: Set<number>;
}) {
  const unmatched: number[] = [];
  for (let i = 0; i < billLineItems.length; i++) {
    if (!matched.has(i)) unmatched.push(i);
  }
  if (unmatched.length === 0 || billLineItems.length === 0) return null;
  return (
    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
      {unmatched.length} bill line{unmatched.length > 1 ? 's' : ''} not matched to
      this PO.
    </p>
  );
}

function AvailablePosSection({
  title,
  subtitle,
  pos,
  loading,
  onAllocate,
  busyAllocPoId,
  emptyHint,
}: {
  title: string;
  subtitle?: string;
  pos: PurchaseOrder[];
  loading: boolean;
  onAllocate: (po: PurchaseOrder) => Promise<void>;
  busyAllocPoId: number | null;
  emptyHint: string;
}) {
  return (
    <Section title={title} subtitle={subtitle}>
      {loading && <p className="text-xs italic text-muted-foreground">Loading…</p>}
      {!loading && pos.length === 0 && (
        <p className="text-xs italic text-muted-foreground">{emptyHint}</p>
      )}
      {pos.length > 0 && (
        <ul className="space-y-1.5">
          {pos.map((po) => (
            <li
              key={po.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-2 py-1.5"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-xs font-medium">
                    {po.purchase_order_number || `PO #${po.id}`}
                  </span>
                  <SourceBadge source={po.source} />
                  <StatusBadge status={po.status} />
                  <PaymentStatusBadge
                    status={po.payment_status}
                    paid={po.amount_paid}
                    total={po.total}
                    currency={po.currency_code}
                  />
                </div>
                <p className="truncate text-[11px] text-muted-foreground">
                  {po.contact_name || 'Unknown supplier'} · {po.currency_code} {po.total}
                  {po.date ? ` · ${po.date}` : ''}
                </p>
              </div>
              <button
                type="button"
                disabled={busyAllocPoId === po.id}
                onClick={() => void onAllocate(po)}
                className={btnPrimary}
              >
                {busyAllocPoId === po.id ? '…' : 'Allocate'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function CreatePoForm({
  bill,
  jobId,
  onSubmit,
}: {
  bill: BillDetail;
  jobId: number | null;
  onSubmit: (input: CreatePurchaseOrderInput) => Promise<void>;
}) {
  const [poNumber, setPoNumber] = useState('');
  const [reference, setReference] = useState(bill.reference || '');
  const [supplier, setSupplier] = useState(bill.vendor_name || '');
  const [total, setTotal] = useState(bill.total || '');
  const [currency, setCurrency] = useState(bill.currency || 'AUD');
  const [date, setDate] = useState(bill.invoice_date || '');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        purchase_order_number: poNumber.trim() || null,
        reference: reference.trim() || null,
        contact_name: supplier.trim() || null,
        total: total || '0.00',
        currency_code: currency || 'AUD',
        date: date || null,
        status: 'AUTHORISED',
        // Seed a single line with the bill's first line description to
        // give the bookkeeper something tangible to refine.
        line_items: bill.line_items?.length
          ? (bill.line_items.slice(0, 1).map((li) => ({
              description: typeof li.description === 'string' ? li.description : '',
              quantity: li.quantity != null ? String(li.quantity) : null,
              unit_amount: li.unit_amount != null ? String(li.unit_amount) : null,
              line_amount: li.line_total != null ? String(li.line_total) : null,
            })) as PurchaseOrderLine[])
          : [],
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 space-y-2 rounded-md border border-border bg-background p-2"
    >
      {!jobId && (
        <p className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          No job linked — the PO will be saved without a job. Link the bill to a
          job first if you want this PO to appear in that job's queue.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field label="PO number">
          <input
            className={inputCls}
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="PO-1234"
          />
        </Field>
        <Field label="Reference">
          <input
            className={inputCls}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Supplier">
        <input
          className={inputCls}
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Total">
          <input
            className={inputCls + ' tabular-nums'}
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <input
            className={inputCls}
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Date">
          <input
            type="date"
            className={inputCls}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
      </div>
      <button type="submit" disabled={submitting} className={btnPrimary}>
        {submitting ? 'Creating…' : 'Create PO'}
      </button>
    </form>
  );
}

/* ─────────────────────────── Helpers ───────────────────────────── */

/**
 * Pair bill lines to PO lines greedily by description fuzzy match,
 * falling back to positional order when descriptions are missing or
 * neither side wins a match.
 *
 * Returns the matches in ``BillPoLineMatch`` shape so the result can
 * be sent straight to ``upsertBillPoAllocation``.
 *
 * Naïve O(n*m) scan but n + m << 100 in practice (bills rarely have
 * more than ~30 lines). Bookkeeper can override per-line via the
 * variance grid in a future iteration.
 */
export function autoPairLines(
  billLines: BillLineItem[],
  poLines: PurchaseOrderLine[],
): BillPoLineMatch[] {
  const matches: BillPoLineMatch[] = [];
  const usedPoIdx = new Set<number>();
  for (let bi = 0; bi < billLines.length; bi++) {
    const bDesc = (billLines[bi]?.description || '').toLowerCase().trim();
    let chosen = -1;
    if (bDesc) {
      for (let pi = 0; pi < poLines.length; pi++) {
        if (usedPoIdx.has(pi)) continue;
        const pDesc = String(poLines[pi]?.description || '').toLowerCase().trim();
        if (!pDesc) continue;
        if (pDesc === bDesc || pDesc.includes(bDesc) || bDesc.includes(pDesc)) {
          chosen = pi;
          break;
        }
      }
    }
    if (chosen < 0) {
      for (let pi = 0; pi < poLines.length; pi++) {
        if (!usedPoIdx.has(pi)) {
          chosen = pi;
          break;
        }
      }
    }
    if (chosen >= 0) {
      usedPoIdx.add(chosen);
      const bl = billLines[bi];
      matches.push({
        bill_line_idx: bi,
        po_line_idx: chosen,
        qty_billed: bl?.quantity != null ? String(bl.quantity) : null,
        unit_price_billed: bl?.unit_amount != null ? String(bl.unit_amount) : null,
      });
    }
  }
  return matches;
}
